import { useCallback, useEffect, useState } from "react";
import { Loader2, Inbox, FlaskConical } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const Logo = ({ size = 48, className = "" }) => (
  <img src="/logo.png" alt="GIA CARE+" width={size} height={size} className={`rounded-full ${className}`} data-testid="brand-logo" />
);

export function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white" data-testid="splash-screen">
      <Logo size={150} className="floaty" />
      <div className="mt-6 h-1 w-24 overflow-hidden rounded-full bg-slate-100"><div className="h-full w-1/2 animate-pulse rounded-full gradient-brand" /></div>
    </div>
  );
}

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  AVAILABLE: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  CONFIRMED: "bg-blue-50 text-blue-700 ring-blue-200",
  COMPLETED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  PAID: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  DEMO_ACTIVE: "bg-amber-50 text-amber-800 ring-amber-200",
  RESERVED: "bg-blue-50 text-blue-700 ring-blue-200",
  ACTIVATED: "bg-purple-50 text-purple-700 ring-purple-200",
  REQUESTED: "bg-purple-50 text-purple-700 ring-purple-200",
  PAYMENT_PENDING: "bg-amber-50 text-amber-800 ring-amber-200",
  REGISTERED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  PAID_NEEDS_REVIEW: "bg-red-50 text-red-700 ring-red-200",
  PENDING: "bg-amber-50 text-amber-800 ring-amber-200",
  PENDING_PAYMENT: "bg-amber-50 text-amber-800 ring-amber-200",
  INITIATED: "bg-amber-50 text-amber-800 ring-amber-200",
  DRAFT: "bg-slate-100 text-slate-700 ring-slate-200",
  NOT_SUBMITTED: "bg-slate-100 text-slate-700 ring-slate-200",
  INACTIVE: "bg-slate-100 text-slate-600 ring-slate-200",
  ARCHIVED: "bg-slate-100 text-slate-500 ring-slate-200",
  SUSPENDED: "bg-orange-50 text-orange-700 ring-orange-200",
  EXPIRED: "bg-slate-100 text-slate-500 ring-slate-200",
  CANCELLED: "bg-slate-100 text-slate-500 ring-slate-200",
  NO_SHOW: "bg-orange-50 text-orange-700 ring-orange-200",
  VOIDED: "bg-red-50 text-red-700 ring-red-200",
  REJECTED: "bg-red-50 text-red-700 ring-red-200",
};

export const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId || `status-${String(status).toLowerCase()}`}
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold ring-1 ring-inset ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}>
    {String(status).replace(/_/g, " ")}
  </span>
);

export const DemoBadge = ({ className = "" }) => (
  <span data-testid="demo-badge" className={`inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-amber-950 ${className}`}>
    <FlaskConical size={11} /> Demo
  </span>
);

export const DemoNotice = ({ children }) => (
  <div data-testid="demo-notice" className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-relaxed text-amber-900">
    <FlaskConical size={16} className="mt-0.5 shrink-0" />
    <div>{children}</div>
  </div>
);

export const Spinner = ({ label = "Loading" }) => (
  <div data-testid="loading-state" className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
    <Loader2 className="animate-spin text-purple-600" size={18} /> {label}…
  </div>
);

export const Empty = ({ title, hint, children }) => (
  <div data-testid="empty-state" className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-12 text-center">
    <Inbox className="mb-3 text-slate-300" size={32} />
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    {hint && <p className="mt-1 max-w-sm text-xs text-slate-500">{hint}</p>}
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export const ErrorBox = ({ error }) =>
  error ? (
    <div data-testid="error-message" className="rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-xs font-medium text-red-700">{error}</div>
  ) : null;

export const PageHead = ({ eyebrow, title, sub, actions }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="page-title">{title}</h1>
      {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="label">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

export function useFetch(url, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const res = await api.get(url);
      setData(res.data);
      setError("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, error, loading, reload: load, setData };
}

export const Meter = ({ used, total, testId }) => {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" data-testid={testId}>
      <div className="h-full rounded-full gradient-brand transition-[width] duration-700" style={{ width: `${100 - pct}%` }} />
    </div>
  );
};
